import React, { useMemo, useState, useEffect } from 'react';
import { get } from 'lodash';
import { useFormContext, Controller, useWatch } from 'react-hook-form';

import { AutoNumeric } from '../../../../common/auto-numeric/AutoNumeric';
import { FieldWithTooltip } from '../../../../common/field-with-tooltip/FieldWithTooltip';
import { CommonProps } from './pricing-list/PricingItem';
import { PoStatus } from '../../../../../domain/PurchaseOrder/PoStatus';
import { PoFormLabel, pricingConfig } from '../po-form-config';
import { useUserPermissions } from '../../../../../context/user/PermissionsProvider';
import { FormPricingKeys } from './pricing-list/FormPricing';
import { useAutoPricingState } from './context/AutoPricingProvider';
import { initialAutoPricingState } from '../../../../../constants/constants';

const { min, max } = pricingConfig[FormPricingKeys.WeeklyTerm].validations;

export const weeklyTermTestId = 'WeeklyTermComponent';

export const WeeklyTerm = (props: CommonProps) => {
  const { index, field, purchaseOrder, state } = props;
  const { hasAutoPricing, hasEditablePricing } = useUserPermissions();
  const { autoPricings, isChanged } = useAutoPricingState();
  const { errors, setValue } = useFormContext();
  const { weeklyTerm } = autoPricings[state] || initialAutoPricingState;
  const fieldName = `pricing[${index}].weeklyTerm`;
  const errorType = get(errors, `${fieldName}.type`);
  const isDraft = purchaseOrder?.poStatus === PoStatus.DRAFT;
  const isSubmitted = purchaseOrder?.poStatus === PoStatus.SUBMITTED;

  const [disabled, setDisabled] = useState(
    hasAutoPricing ? isSubmitted || !isDraft : isSubmitted
  );

  const weeklyTermWatched = useWatch({ name: `pricing[${index}].weeklyTerm` });

  useEffect(() => {
    if (weeklyTermWatched) {
      setValue(fieldName, weeklyTermWatched, {
        shouldDirty: field.weeklyTerm === '',
        shouldValidate: field.weeklyTerm === ''
      });

      if (disabled && hasAutoPricing) {
        setDisabled(purchaseOrder?.poStatus === PoStatus.SUBMITTED);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeklyTermWatched]);

  return useMemo(
    () => (
      <div data-testid={weeklyTermTestId}>
        <FieldWithTooltip
          info={`Please add weekly term value between ${min} and ${max}`}
          id={fieldName}
          title={`${PoFormLabel.WeeklyTerm}:`}
          required={true}
        >
          <Controller
            as={AutoNumeric}
            name={fieldName}
            defaultValue={field.weeklyTerm}
            placeholder="--"
            options={{
              minimumValue: 0,
              decimalPlaces: 0,
              restrictZeroFirstChar: false,
              maximumValue: max
            }}
            disabled={
              (hasAutoPricing && !hasEditablePricing) || disabled || isChanged
            }
            rules={{ required: true, min, max }}
            hasError={!!errorType}
            {...((!!weeklyTerm || isChanged) && {
              imperativeValue: isChanged ? '' : weeklyTerm?.toString(),
              imperativeMode: true
            })}
          />
        </FieldWithTooltip>
      </div>
    ),
    [
      fieldName,
      field.weeklyTerm,
      hasAutoPricing,
      hasEditablePricing,
      disabled,
      isChanged,
      errorType,
      weeklyTerm
    ]
  );
};
